import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Title, Badge, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDate, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * The plan of a business that pays by invoice (owner, 18 October 2026): "clients that are on manual billing
 * (invoicing) I need to be able to change plans for them". With the switch on, its plan fee is taken from the credit
 * added for its EFTs (Add credit, a payment they made), on the 1st and for an upgrade at once, and only this card
 * changes its plan. Each choice is quoted first, so the confirm says exactly what happens and what is taken.
 */
/** The choices their team is emailed about; keeping a plan or ending an unpaid one sends nothing. */
const EMAILED = new Set(['start', 'upgrade', 'downgrade', 'cancel']);

export function InvoicePlanCard({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [choice, setChoice] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const planQ = useQuery({ queryKey: ['admin', 'org-plan', tenantId], queryFn: () => admin.getOrganisationPlan(tenantId), retry: false });
  const plansQ = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => admin.listPlans(), staleTime: 60_000 });
  const p = planQ.data?.data;
  const plans = (plansQ.data?.data ?? []).filter((x) => x.is_active).sort((a, b) => a.sort_order - b.sort_order);

  const refresh = () => {
    for (const key of [['admin', 'org-plan', tenantId], ['admin', 'wallet-alert', tenantId], ['admin', 'subscriptions'], ['admin', 'ledger']])
      qc.invalidateQueries({ queryKey: key });
  };

  const invoice = useMutation({
    mutationFn: (on: boolean) => admin.setPaysByInvoice(tenantId, on),
    onSuccess: (res) => {
      refresh();
      notifySuccess(res.data.pays_by_invoice ? t('tenants.view.invoicePlan.switchedOn', { name: tenantName }) : t('tenants.view.invoicePlan.switchedOff', { name: tenantName }), t('tenants.view.invoicePlan.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('tenants.view.invoicePlan.title'))
  });

  const choose = useMutation({
    mutationFn: (code: string) => admin.chooseOrganisationPlan(tenantId, code),
    onSuccess: (res) => {
      refresh();
      setChoice('');
      notifySuccess(t(`tenants.view.invoicePlan.done.${res.data.action}`, { plan: res.data.plan_name, current: res.data.current_plan_name ?? '' }), t('tenants.view.invoicePlan.title'));
    },
    onError: (e) => setRefusal(isApiError(e) ? e.message : e instanceof Error ? e.message : t('common.unknownError'))
  });

  const quote = useMutation({
    mutationFn: (code: string) => admin.quoteOrganisationPlan(tenantId, code),
    onSuccess: (res) => {
      const q = res.data;
      const v = {
        plan: q.plan_name, current: q.current_plan_name ?? '',
        amount: formatMinor(q.amount_minor, q.currency),
        // Plan credit is counted in credits, not rand (one credit is R1 of messaging).
        credits: t('tenants.view.invoicePlan.credits', { count: (q.credits_minor / 100).toLocaleString('en-ZA', { maximumFractionDigits: 2 }) }),
        available: formatMinor(q.available_minor, q.currency), date: q.effective_at ? formatDate(q.effective_at) : ''
      };
      // Nothing to confirm: say why, in the card.
      if (q.action === 'none' || q.action === 'past_due' || !q.enough) {
        setRefusal(!q.enough && q.action !== 'past_due' ? t('tenants.view.invoicePlan.short', v) : t(`tenants.view.invoicePlan.quote.${q.action}`, v));
        return;
      }
      openConfirmModal({
        title: t('tenants.view.invoicePlan.confirm.title', { plan: q.plan_name, name: tenantName }),
        children: (
          <Stack gap="xs">
            <Text role="bodySmall">{t(`tenants.view.invoicePlan.quote.${q.action}`, v)}</Text>
            {q.amount_minor > 0 && <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.invoicePlan.confirm.credit', v)}</Text>}
            {EMAILED.has(q.action) && <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.invoicePlan.confirm.email')}</Text>}
          </Stack>
        ),
        labels: { confirm: t('tenants.view.invoicePlan.confirm.submit'), cancel: t('action.cancel') },
        onConfirm: () => choose.mutate(q.plan_code)
      });
    },
    onError: (e) => setRefusal(e instanceof Error ? e.message : t('common.unknownError'))
  });

  const sub = p?.subscription ?? null;
  const state = !p ? '' : !sub
    ? t('tenants.view.invoicePlan.state.none')
    : sub.status === 'past_due'
      ? t('tenants.view.invoicePlan.state.pastDue', { plan: sub.plan_name ?? '', date: formatDate(sub.billing_cycle_end) })
      : sub.ends_at
        ? t('tenants.view.invoicePlan.state.ends', { plan: sub.plan_name ?? '', date: formatDate(sub.ends_at) })
        : sub.pending_plan_name
          ? t('tenants.view.invoicePlan.state.changes', { plan: sub.plan_name ?? '', next: sub.pending_plan_name, date: formatDate(sub.billing_cycle_end) })
          : t('tenants.view.invoicePlan.state.renews', { plan: sub.plan_name ?? '', date: formatDate(sub.billing_cycle_end) });

  return (
    <Card withBorder data-testid="invoice-plan">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Title order={4}>{t('tenants.view.invoicePlan.title')}</Title>
          {p?.pays_by_invoice && <Badge size="sm" variant="light" color="teal">{t('tenants.view.invoicePlan.badge')}</Badge>}
        </Group>
        {planQ.error && <Alert color="warning">{t('tenants.view.invoicePlan.loadError')}</Alert>}
        {p && (
          <Switch
            checked={p.pays_by_invoice}
            onChange={(on) => { setRefusal(null); invoice.mutate(on); }}
            disabled={invoice.isPending}
            label={t('tenants.view.invoicePlan.switch')}
            description={t('tenants.view.invoicePlan.switchHint')}
            data-testid="invoice-switch"
          />
        )}
        {p && (
          <Stack gap={4}>
            <Text role="bodySmall" data-testid="invoice-plan-state">{state}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.invoicePlan.available', { amount: formatMinor(p.available_minor, p.currency) })}</Text>
          </Stack>
        )}
        {p?.pays_by_invoice && (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label={t('tenants.view.invoicePlan.choose')}
              value={choice}
              onChange={(e) => { setChoice(e.currentTarget.value); setRefusal(null); }}
              data={[{ value: '', label: t('tenants.view.invoicePlan.choosePlaceholder') },
                ...plans.map((x) => ({ value: x.code, label: `${x.name} (${x.monthly_amount_minor > 0 ? t('tenants.view.invoicePlan.perMonth', { amount: formatMinor(x.monthly_amount_minor, x.currency) }) : t('tenants.view.invoicePlan.free')})` }))]}
              style={{ minWidth: 260 }}
              data-testid="invoice-plan-select"
            />
            <Button size="sm" disabled={!choice} loading={quote.isPending || choose.isPending} onClick={() => { setRefusal(null); quote.mutate(choice); }} data-testid="invoice-plan-change">
              {t('tenants.view.invoicePlan.change')}
            </Button>
          </Group>
        )}
        {refusal && <Alert color="warning" data-testid="invoice-plan-refusal">{refusal}</Alert>}
      </Stack>
    </Card>
  );
}
